/**
 * Union type for all valid operator keys.
 */
export type OperatorKey =
  | "is"
  | "is_not"
  | "contains"
  | "greater_than"
  | "less_than"
  | "greater_than_or_equal"
  | "less_than_or_equal"
  | "within_next"
  | "within_last"
  | "is_null"
  | "not_null"
  | "is_true"
  | "is_false";

/**
 * Human-readable labels for rule operators.
 * Used in RuleCondition (editing) and SegmentViewContent (display).
 */
export const operatorLabels: Record<OperatorKey, string> = {
  is: "Is",
  is_not: "Is not",
  contains: "Contains",
  greater_than: "Greater than",
  less_than: "Less than",
  greater_than_or_equal: "Greater than or equal",
  less_than_or_equal: "Less than or equal",
  within_next: "Within the next",
  within_last: "Within the last",
  is_null: "Is empty",
  not_null: "Is not empty",
  is_true: "Is true",
  is_false: "Is false",
};

/**
 * Get the human-readable label for an operator.
 * Returns the operator key itself as fallback if not found.
 */
export const getOperatorLabel = (operator: string): string =>
  operatorLabels[operator as OperatorKey] || operator;

/**
 * Operators that do not require a value input.
 * Used in RuleCondition (to hide value field) and RulePreview (for validation).
 */
export const VALUE_LESS_OPERATORS = [
  "is_null",
  "not_null",
  "is_true",
  "is_false",
] as const;

/**
 * Segment-specific colors not available in the Mantine theme.
 * For primary/accent colors, use theme.colors["jem-theme"] instead.
 */
export const SEGMENT_STATUS_COLORS = {
  activeBackground: "#e8faf5",
  activeText: "#14926d",
  inactiveBackground: "rgba(6, 33, 51, 0.1)",
  inactiveText: "#829099",
} as const;

export const SEGMENT_PREVIEW_COLORS = {
  background: "#fff4ea",
  text: "#ff922b",
} as const;

/**
 * Bounded, self-updating relative-date operators (ENG-4944 / ENG-4971).
 * The operator carries the direction and both bounds:
 *   - within_next: today .. today + value   (e.g. terminating in the next 3 months)
 *   - within_last: today - value .. today   (the honest version of the old "ago")
 * The value stays a plain period token (e.g. "3m") — the operator, not a sign, sets
 * the direction. Because these are bounded windows, the display omits the "ago" suffix.
 */
export const WINDOW_DATE_OPERATORS = ["within_next", "within_last"] as const;

/**
 * True when an operator is a bounded relative-date window (within_next/within_last),
 * which reads as "Within the next/last 3 months" and therefore needs no "ago" suffix.
 */
export const isWindowDateOperator = (operator: string): boolean =>
  (WINDOW_DATE_OPERATORS as readonly string[]).includes(operator);

/**
 * Operators for relative date mode (a time period relative to today).
 * The bounded window operators are preferred; the legacy open-ended "ago" operators
 * (greater_than_or_equal / less_than_or_equal) remain for existing segments.
 */
export const RELATIVE_DATE_OPERATORS = [
  "within_next",
  "within_last",
  "greater_than_or_equal",
  "less_than_or_equal",
  "is_null",
  "not_null",
] as const;

/**
 * Operators for exact date mode (comparing to a specific date).
 */
export const EXACT_DATE_OPERATORS = [
  "is",
  "is_not",
  "greater_than_or_equal",
  "less_than_or_equal",
  "greater_than",
  "less_than",
  "is_null",
  "not_null",
] as const;

/**
 * Relative date units for date comparison operators.
 * Used in RelativeDateInput for greater_than/less_than date rules.
 */
export const RELATIVE_DATE_UNITS = [
  { value: "d", label: "Days" },
  { value: "w", label: "Weeks" },
  { value: "m", label: "Months" },
  { value: "y", label: "Years" },
] as const;

export type RelativeDateUnit = (typeof RELATIVE_DATE_UNITS)[number]["value"];

/**
 * Regex pattern to validate relative date format (e.g., "30d", "2w", "3m", "1y").
 */
export const RELATIVE_DATE_PATTERN = /^(\d+)([dwmy])$/;

/**
 * Human-readable labels for relative date units (singular and plural).
 */
export const RELATIVE_DATE_UNIT_LABELS: Record<
  RelativeDateUnit,
  { singular: string; plural: string }
> = {
  d: { singular: "day", plural: "days" },
  w: { singular: "week", plural: "weeks" },
  m: { singular: "month", plural: "months" },
  y: { singular: "year", plural: "years" },
};

/**
 * Parse a relative date string (e.g., "30d") into amount and unit.
 */
export const parseRelativeDate = (
  value: string
): { amount: number; unit: RelativeDateUnit } | null => {
  const match = RELATIVE_DATE_PATTERN.exec(value);
  if (!match) return null;
  return {
    amount: parseInt(match[1], 10),
    unit: match[2] as RelativeDateUnit,
  };
};

/**
 * Format a relative date value as a bare period, without any direction suffix.
 * Examples: "1d" -> "1 day", "3m" -> "3 months"
 */
export const formatRelativePeriod = (value: string): string => {
  const parsed = parseRelativeDate(value);
  if (!parsed) return value;
  const unitLabels = RELATIVE_DATE_UNIT_LABELS[parsed.unit];
  const unitLabel =
    parsed.amount === 1 ? unitLabels.singular : unitLabels.plural;
  return `${parsed.amount} ${unitLabel}`;
};

/**
 * Format a relative date value for display, pairing with its operator.
 * The bounded window operators (within_next/within_last) already read as
 * "Within the next/last …", so they show a bare period. The legacy open-ended
 * operators keep the "ago" suffix.
 * Examples: ("3m", "within_next") -> "3 months"; ("30d") -> "30 days ago"
 */
export const formatRelativeDateDisplay = (
  value: string,
  operator?: string
): string => {
  const parsed = parseRelativeDate(value);
  if (!parsed) return value;
  const period = formatRelativePeriod(value);
  return operator && isWindowDateOperator(operator) ? period : `${period} ago`;
};

/**
 * Get operator label for date fields with relative dates.
 * Maps technical operators to user-friendly labels.
 */
export const getDateOperatorLabel = (operator: string): string => {
  const dateOperatorLabels: Record<string, string> = {
    greater_than: "More than",
    less_than: "Less than",
    greater_than_or_equal: "More than or equal to",
    less_than_or_equal: "Less than or equal to",
    within_next: "Within the next",
    within_last: "Within the last",
    is: "Is",
    is_not: "Is not",
    is_null: "Is empty",
    not_null: "Is not empty",
  };
  return dateOperatorLabels[operator] || operator;
};
